import React, { useCallback, useState } from "react";
import { Pressable, ScrollView, StyleSheet } from "react-native";
import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutDown,
} from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { styleConstants } from "~/style/styleConstants";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { AlertContext } from "./AlertContext";
import type { ContextType } from "./AlertContext";
import type { IconProps } from "../Icon";
import type { AlertButton } from "react-native";

const defaultOptions = {
  cancelable: false,
  onDismiss: () => {},
  buttonsContainerStyle: {},
};

const S = StyleSheet.create({
  flex: { flex: 1 },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: styleConstants.colorBackgroundBackdrop,
  },

  popup: {
    maxHeight: "80%",
    width: "80%",
    borderRadius: 4,
    backgroundColor: styleConstants.colorBackgroundWhite,
    padding: styleConstants.spacingLarge,
  },
});

interface AlertProviderProps {
  children: React.ReactNode;
}

export function AlertProvider({ children, ...otherProps }: AlertProviderProps) {
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertParams, setAlertParams] = useState({
    title: "",
    body: "",
    buttons: [] as AlertButton[],
    options: defaultOptions,
    iconName: "" as IconProps["name"] | undefined,
  });

  const alert: ContextType["alert"] = useCallback(
    (
      title = "",
      body = "",
      buttons = [],
      iconName,
      options = defaultOptions,
    ) => {
      const preparedOptions = { ...defaultOptions, ...options };
      setAlertParams({
        title,
        body,
        buttons,
        options: preparedOptions,
        iconName,
      });
      setAlertVisible(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const contextValue = { alertVisible, setAlertVisible, alert };

  const { title, body, buttons, options, iconName } = alertParams;

  const { onDismiss, cancelable, buttonsContainerStyle } = options;

  const handleModalPress = () => {
    if (cancelable) {
      onDismiss();
      setAlertVisible(false);
    }
  };

  const buttonsRender = buttons.map((button, index) => {
    return (
      <React.Fragment key={button.text}>
        {index !== 0 && <Spacer medium />}
        <Button
          outline={button.style === "cancel"}
          title={button.text}
          onPress={() => {
            setAlertVisible(false);
            return button.onPress?.();
          }}
          style={{ flex: 1 }}
          colorDanger={button.style === "destructive"}
        />
      </React.Fragment>
    );
  });

  return (
    <AlertContext.Provider value={contextValue} {...otherProps}>
      {children}
      {alertVisible && (
        <Modal>
          <Animated.View style={S.backdrop} entering={FadeIn} exiting={FadeOut}>
            <Pressable style={S.flex} onPress={handleModalPress} />
          </Animated.View>
          <View flex centerContent pointerEvents="box-none">
            {/* Popup / Modal container */}
            <Animated.View
              entering={FadeInDown}
              exiting={FadeOutDown}
              style={S.popup}
            >
              {iconName && (
                <View centerContent>
                  <Icon name={iconName} />
                </View>
              )}
              <ScrollView bounces={false} showsVerticalScrollIndicator={false}>
                {!!title && (
                  <View paddingVerticalMedium>
                    <Text weightExtraBold sizeLarge alignCenter>
                      {title}
                    </Text>
                  </View>
                )}

                {!!body && (
                  <View paddingVerticalMedium>
                    <Text alignCenter sizeLarge weightRegular>
                      {body}
                    </Text>
                  </View>
                )}
              </ScrollView>

              <Spacer />

              <View
                flexDirectionRow
                justifyContentCenter
                style={buttonsContainerStyle}
              >
                {buttonsRender.length === 0 ? (
                  <Button
                    transparent
                    title={intl.formatMessage({
                      id: "C3dd3n",
                      defaultMessage: "OK",
                      description: "Alert button",
                    })}
                    onPress={() => {
                      setAlertVisible(false);
                    }}
                  />
                ) : (
                  buttonsRender
                )}
              </View>
            </Animated.View>
          </View>
        </Modal>
      )}
    </AlertContext.Provider>
  );
}
